import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CREATABLE_LIFECYCLE_PHASES,
  LIFECYCLE_PHASE_LABELS,
  LifecyclePhase,
  PRODUCTION_FORMAT_LABELS,
  PROJECT_DEFINITION,
  ProductionFormat,
  ProductionModel,
  projectSlug,
} from '@scoutaround/script-breakdown-shared';

import { api, onSessionExpired } from '../lib/api.js';
import { Icon, type IconName } from '../components/Icon.js';
import headerArt from '../assets/new-project-header.webp';
import { PaymentPanel, PlanChooser, money, termLabel } from '../components/Plans.js';
import { QueryState } from '../components/QueryState.js';
import { StatusPill } from '../components/ui/StatusPill.js';

/** A glyph per format. Typed against the enum, so a new format cannot ship
 *  without one — the same rule that now governs the labels in the contract. */
export const FORMAT_ICON: Record<ProductionFormat, IconName> = {
  FEATURE_FILM: 'feature',
  SERIES: 'series',
  COMMERCIAL: 'commercial',
  MUSIC_VIDEO: 'musicVideo',
  MICRO_CONTENT: 'microContent',
  DOCUMENTARY: 'documentary',
  OTHER: 'other',
};

const MODEL_LABEL: Record<ProductionModel, string> = {
  TRADITIONAL: 'Traditional',
  HYBRID: 'Hybrid',
  AI: 'AI',
};

/** One word each, and the word alone says nothing — the design explains all
 *  three, and this is the only place in the product that does. */
const MODEL_DESCRIPTION: Record<ProductionModel, string> = {
  TRADITIONAL: 'Physical filming on real locations',
  HYBRID: 'Physical production + AI digital twins',
  AI: 'Fully virtual production with AI locations',
};

/** Numbered in the UI, so the label is all that is written down here. */
const STEPS = ['Project basics', 'Choose a plan'] as const;

/** The right-hand column: what the workspace is for, in the order it happens. */
const PROMISES: readonly { label: string; icon: IconName }[] = [
  { label: 'Add your script or brief', icon: 'script' },
  { label: 'Define the locations you need', icon: 'pin' },
  { label: 'Scout and shortlist options', icon: 'search' },
  { label: 'Keep your decisions in one place', icon: 'star' },
];

/**
 * "SAVE AND EXIT" HAS TO BE TRUE.
 *
 * Nothing is written to the server until the producer commits on step 2 — that
 * is deliberate (see the mutation below), and it is why the way out cannot
 * simply be `onCancel()`: a button that says it saves and then loses the title,
 * the format and the model is worse than one that says Cancel.
 *
 * So the half-filled form is kept in `sessionStorage`: this tab, until it is
 * closed, cleared the moment the production actually exists. Not
 * `localStorage`, because a draft is not a document — it should not still be
 * waiting on a shared machine tomorrow.
 *
 * Every access is wrapped: a private window, blocked site data or a full quota
 * all THROW here, and losing a draft must never take the page down with it.
 */
const DRAFT_KEY = 'sa.script-breakdown.new-project.draft';

type Draft = {
  title: string;
  format: ProductionFormat;
  productionModel: ProductionModel;
  lifecyclePhase: LifecyclePhase;
};

function readDraft(): Draft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);

    if (raw === null) return null;

    const parsed: unknown = JSON.parse(raw);

    if (typeof parsed !== 'object' || parsed === null) return null;

    const d = parsed as Partial<Draft>;

    // The shape is validated against the CONTRACT, not trusted: a draft
    // written by an older build can name a format this one no longer has, and
    // an unknown value would reach the API as a 422 the producer cannot act on.
    return {
      title: typeof d.title === 'string' ? d.title : '',
      format: ProductionFormat.safeParse(d.format).data ?? 'FEATURE_FILM',
      productionModel: ProductionModel.safeParse(d.productionModel).data ?? 'TRADITIONAL',
      lifecyclePhase: LifecyclePhase.safeParse(d.lifecyclePhase).data ?? 'IDEA',
    };
  } catch {
    return null;
  }
}

function writeDraft(draft: Draft): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Nothing to do and nothing to say: the exit still works.
  }
}

function clearDraft(): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    // As above.
  }
}

/**
 * CREATING A PRODUCTION IS A PAGE, NOT A DIALOG.
 *
 * This was a modal: a two-step flow — a form, then a plan catalogue with prices
 * and payment — inside a fixed 760px box that scrolled inside a page that also
 * scrolled. On a phone that is the worst shape a flow can have. The keyboard
 * covers half of it, the two selects sit at half width each because the box
 * cannot reflow, the plan cards are read through a letterbox, and the way out
 * is an ✕ the thumb cannot reach.
 *
 * THE STEPS UNMOUNT, AND THAT IS SAFE — every field here is controlled, so
 * what was typed lives in this component's state and not in the DOM. It used
 * to be hidden instead, which was the right call for an uncontrolled form and
 * the wrong one for this: both steps in the tree at once meant two elements
 * answering to every query, two primary buttons, and a step-2 layout that had
 * to fit inside step 1's column.
 */
export function NewProjectPage({
  onCreated,
  onCancel,
  step: stepFromAddress,
  onStep,
}: {
  onCreated: (address: string) => void;
  onCancel: () => void;
  /** The step, when the address owns it: `/projects/new` is 1, `/projects/new/plan` is 2. */
  step?: 1 | 2;
  onStep?: (step: 1 | 2) => void;
}) {
  const queryClient = useQueryClient();
  // Read ONCE, in the initialiser: re-reading on every render would fight the
  // producer's own typing.
  const [draft] = useState(readDraft);
  const [restored, setRestored] = useState(draft !== null);
  const [ownStep, setOwnStep] = useState<1 | 2>(1);
  const step = stepFromAddress ?? ownStep;
  const setStep = (next: 1 | 2) => {
    setOwnStep(next);
    onStep?.(next);
  };
  const [title, setTitle] = useState(draft?.title ?? '');
  /** Set only when somebody actually presses Continue — a field that goes red
   *  before it has been left alone is telling somebody off for typing slowly. */
  const [missingTitle, setMissingTitle] = useState(false);
  const [format, setFormat] = useState<ProductionFormat>(draft?.format ?? 'FEATURE_FILM');
  const [productionModel, setProductionModel] = useState<ProductionModel>(
    draft?.productionModel ?? 'TRADITIONAL',
  );
  /** IDEA, because that is where a production being created for the first time
   *  actually is. Defaulting to development quietly claimed work that had not
   *  happened — and the phase decides which tabs the workspace shows. */
  const [lifecyclePhase, setLifecyclePhase] = useState<LifecyclePhase>(
    draft?.lifecyclePhase ?? 'IDEA',
  );

  // Step 2. `null` is Free — the tier with no purchase to make.
  const [chosen, setChosen] = useState<{ code: string; term: number } | null>(null);
  const [payFor, setPayFor] = useState<{ projectId: string; address: string } | null>(null);

  // A link straight to the plan step with no production named yet is sent back
  // to the basics — the plan is chosen FOR something.
  useEffect(() => {
    if (step === 2 && title.trim() === '') setStep(1);
    // On mount only: the guard is about how the page was entered.
  }, []);

  const plans = useQuery({ queryKey: ['plans'], queryFn: api.plans, enabled: step === 2 });
  const catalogue = plans.data?.plans ?? [];
  const chosenPlan = catalogue.find((p) => p.code === chosen?.code) ?? null;
  const chosenPrice = chosenPlan?.prices.find((p) => p.termMonths === chosen?.term) ?? null;

  /**
   * THE PROJECT IS CREATED AT THE END, NOT BETWEEN THE STEPS.
   *
   * Creating it on "Continue" would be simpler — step 2 could then use the
   * ordinary project-scoped billing endpoints — and it would leave an abandoned
   * production behind every time somebody opened the page, read the prices and
   * closed it. So the catalogue is fetched without a project (`/v1/plans`), and
   * nothing is written until the producer commits.
   *
   * A PAID CHOICE STILL CREATES THE PROJECT FIRST, because a purchase is
   * attached to a production: `startPurchase` needs its id. If the card is then
   * declined or abandoned, what remains is a project on Free — which is a valid
   * state the product already has a name for, not wreckage. The alternative,
   * taking money before the thing exists, has no good failure mode at all.
   */
  const create = useMutation({
    mutationFn: () => api.createProject({ title, format, productionModel, lifecyclePhase }),
    onSuccess: async (result) => {
      // The production exists, so the draft is not a draft any more.
      clearDraft();
      await queryClient.invalidateQueries({ queryKey: ['projects'] });

      const address = result.urlKey === '' ? result.id : projectSlug(title, result.urlKey);

      if (chosen === null) {
        onCreated(address);

        return;
      }

      setPayFor({ projectId: result.id, address });
    },
  });

  /** Live even with an empty title, so pressing it can EXPLAIN rather than do
   *  nothing. Disabled only while a create is actually in flight, where a
   *  second press would be a second production. */
  const submitEnabled = !create.isPending;

  /**
   * BEING THROWN OUT IS NOT THE SAME AS LEAVING.
   *
   * When the server refuses the token, `App` replaces this page with "Your
   * session has ended" — correctly, because there is no session. But the
   * producer did not choose to go: they had typed a title, picked a format and
   * pressed Continue, and the first request of step 2 is where the dead token
   * is discovered. Signing back in returns them here, and without this it
   * returns them to an empty form.
   *
   * The same draft the exit button writes, written for them. A ref for the
   * values so the listener is registered once and still sees what is current —
   * re-subscribing on every keystroke would be a new listener per character.
   */
  const current = useRef({ title, format, productionModel, lifecyclePhase });
  current.current = { title, format, productionModel, lifecyclePhase };

  useEffect(
    () =>
      onSessionExpired(() => {
        if (current.current.title.trim() !== '') writeDraft(current.current);
      }),
    [],
  );

  const leave = () => {
    if (title.trim() === '') {
      // An empty form is not a draft, and keeping one means the next visit
      // opens with a stale "picked up where you left off" over nothing.
      clearDraft();
    } else {
      writeDraft({ title, format, productionModel, lifecyclePhase });
    }

    onCancel();
  };

  /**
   * THE PAGE HEADING, RENDERED ONCE.
   *
   * Both steps carry it, and only one step is on screen at a time — so it is
   * built here rather than written twice. Two copies would also mean two live
   * regions announcing the same move, which a screen reader reads out twice.
   */
  const heading = (
    <>
      <h1 className="dh" style={{ fontSize: 'clamp(34px, 5vw, 54px)' }}>
        New project
      </h1>
      <p
        className="mm"
        data-testid="wizard-definition"
        style={{ fontSize: 15, margin: '14px 0 0', maxWidth: '48ch', lineHeight: 1.6 }}
      >
        {step === 1
          ? 'Create the workspace for one production. Start with the basics now — you can add the rest later.'
          : PROJECT_DEFINITION}
      </p>

      {/* THE PROGRESS BAR IS A PICTURE, so it announces nothing. A screen
          reader reading this form top to bottom hears the same fields again
          after Continue with no word about having moved; this is the sentence
          that says so, and it is the only place "Step 1 of 2" is spelled out.
          Off-screen rather than `display: none`, because a hidden live region
          is not announced at all. */}
      <p
        data-testid="wizard-step-label"
        aria-live="polite"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
          margin: -1,
          padding: 0,
          border: 0,
        }}
      >
        {`Step ${String(step)} of ${String(STEPS.length)} — ${STEPS[step - 1] ?? ''}`}
      </p>

      {/* A STEP YOU CAN SEE, not a sentence you have to read: it says what the
          second one IS, which is the part that decides whether somebody starts
          at all. `aria-current` carries the same fact without the colour, and a
          finished step is a tick rather than a number. */}
      <ol
        data-testid="wizard-progress"
        style={{
          listStyle: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          margin: '26px 0 0',
          padding: 0,
          flexWrap: 'wrap',
        }}
      >
        {STEPS.map((label, i) => {
          const n = i + 1;
          const active = step === n;
          const done = step > n;

          return (
            <li
              key={label}
              aria-current={active ? 'step' : undefined}
              data-testid={`wizard-step-${String(n)}`}
              data-on={active ? '1' : '0'}
              data-done={done ? '1' : '0'}
              style={{ display: 'flex', alignItems: 'center', gap: 10 }}
            >
              {i === 0 ? null : (
                <span
                  aria-hidden="true"
                  className="npg-rule"
                  style={{ width: 46, height: 1, background: 'var(--color-divider)' }}
                />
              )}
              <span
                aria-hidden="true"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  background: active
                    ? 'var(--color-accent)'
                    : done
                      ? 'var(--color-accent-50)'
                      : 'var(--color-surface)',
                  color: active ? '#fff' : done ? 'var(--color-accent)' : 'var(--color-neutral-600)',
                  border: active
                    ? 'none'
                    : `1px solid ${done ? 'var(--color-accent-200)' : 'var(--color-divider)'}`,
                }}
              >
                {done ? <Icon name="check" size={14} strokeWidth={2.4} /> : n}
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: active ? 'var(--ink)' : 'var(--color-neutral-600)',
                }}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ol>
    </>
  );

  // DECORATIVE, so `alt` is empty — the words beside it say everything it
  // draws. It is also the first thing to give up its space: the grid collapses
  // below 960px and a phone starts with the words.
  //
  // SMALLER ON STEP 2, and that is a layout fact rather than a taste: there it
  // is the only thing in its column, so it alone sets the height of the row —
  // and at step 1's size it pushed the plan cards a screenful down behind a
  // band of nothing. On step 1 the promise block sits beneath it and the column
  // is full either way. Once the production exists the plan cards are gone and
  // the payment card stands beside it, so it is step 1's size again.
  const big = step === 1 || payFor !== null;
  const art = (
    <figure style={{ margin: 0, minWidth: 0 }}>
      <img
        src={headerArt}
        alt=""
        data-testid="new-project-visual"
        style={{
          width: '100%',
          maxWidth: big ? 440 : 300,
          height: 'auto',
          display: 'block',
          margin: '0 auto',
        }}
      />
    </figure>
  );

  return (
    <div
      data-testid="new-project-page"
      style={{
        // THE PAGE'S OWN GUTTER. Every other screen carries one — the project
        // list, the workspace shell — because this app has no outer container;
        // without it the content sat flush against the left edge of the window,
        // which reads as a broken page rather than a missing rule.
        padding: 'clamp(18px, 3vw, 34px) clamp(18px, 4vw, 40px) 72px',
        maxWidth: 1320,
        margin: '0 auto',
      }}
    >
      <nav className="mm" style={{ fontSize: 12.5, marginBottom: 18 }}>
        <button
          type="button"
          className="npg-crumb"
          data-testid="new-project-back"
          onClick={onCancel}
        >
          Projects
        </button>
        <span aria-hidden="true"> › </span>
        <span style={{ color: 'var(--ink)', fontWeight: 600 }}>New project</span>
      </nav>

      {/* TWO COLUMNS, TWO CELLS — not a grid of rows.
          The heading and the form are one column; the illustration and what it
          illustrates are the other. Laid out as rows instead, the tall picture
          set the height of the first row and the form began below its bottom
          edge, leaving a hand's width of nothing under the progress bar. */}
      <div className="npg-2col">
        <div style={{ minWidth: 0 }}>
          {heading}

          {payFor !== null ? (
            <div data-testid="wizard-payment">
              <PaymentPanel
                projectId={payFor.projectId}
                planCode={chosen?.code ?? ''}
                termMonths={chosen?.term ?? 1}
                lead={
                  <p className="mm" style={{ fontSize: 12.5, margin: '0 0 16px', lineHeight: 1.6 }}>
                    <strong>{title}</strong> is created. Paying now unlocks the plan straight away
                    — and if you close this, the production is still there on Free and you can pay
                    from Plan &amp; billing whenever you like.
                  </p>
                }
                renderActive={(plan) => (
                  <PlanActiveCard
                    title={title}
                    planName={catalogue.find((p) => p.code === plan.planCode)?.name ?? plan.planCode}
                    until={plan.until}
                    onContinue={plan.onContinue}
                  />
                )}
                onCancel={() => {
                  // NOT an error path: the production exists and is usable.
                  // This is somebody deciding to pay later, so it opens the
                  // workspace rather than leaving them in a dialog about a
                  // purchase they have declined.
                  onCreated(payFor.address);
                }}
                onPaid={async () => {
                  await queryClient.invalidateQueries({ queryKey: ['projects'] });
                  onCreated(payFor.address);
                }}
              />
            </div>
          ) : step === 1 ? (
            <form
              style={{ marginTop: 26 }}
              onSubmit={(e) => {
                e.preventDefault();

                // A DISABLED BUTTON EXPLAINS NOTHING, so the button stays live
                // and the refusal happens here, where it can say which field
                // and why.
                if (title.trim() === '') {
                  setMissingTitle(true);

                  return;
                }

                setStep(2);
              }}
            >
              <div className="card1 npg-card" data-testid="wizard-card">
                <h2 className="dh" style={{ fontSize: 26 }}>
                  Project basics
                </h2>
                <p className="mm" style={{ fontSize: 13, margin: '8px 0 0' }}>
                  Only a working title and production format are required.
                </p>

                {/* TEXT MUST NEVER APPEAR FROM NOWHERE. A form that opens already
                    filled in reads as somebody else's work, or as a bug — so the
                    restore says so, and offers the way out in the same line. */}
                {restored ? (
                  <p
                    data-testid="wizard-draft-restored"
                    className="mm"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      flexWrap: 'wrap',
                      fontSize: 12.5,
                      margin: '18px 0 0',
                      padding: '10px 14px',
                      background: 'var(--color-surface)',
                      border: '1px solid var(--color-divider)',
                    }}
                  >
                    <Icon name="check" />
                    Picked up where you left off.
                    <button
                      type="button"
                      className="ctl"
                      data-testid="wizard-draft-discard"
                      onClick={() => {
                        clearDraft();
                        setTitle('');
                        setFormat('FEATURE_FILM');
                        setProductionModel('TRADITIONAL');
                        setLifecyclePhase('IDEA');
                        setMissingTitle(false);
                        setRestored(false);
                      }}
                    >
                      Start over
                    </button>
                  </p>
                ) : null}

                <div style={{ marginTop: 24 }}>
                  <label className="flbl" htmlFor="wiz-title">
                    Working title <span style={{ color: 'var(--color-accent)' }}>*</span>
                  </label>
                  <div style={{ position: 'relative', marginTop: 8 }}>
                    <span
                      style={{
                        position: 'absolute',
                        left: 14,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--color-neutral-500)',
                        display: 'inline-flex',
                        pointerEvents: 'none',
                      }}
                    >
                      <Icon name="title" size={17} />
                    </span>
                    <input
                      id="wiz-title"
                      data-testid="wizard-title"
                      value={title}
                      placeholder="e.g. Northern Light"
                      aria-describedby="wiz-title-help"
                      aria-invalid={missingTitle ? true : undefined}
                      onChange={(e) => {
                        setTitle(e.target.value);
                        if (e.target.value.trim() !== '') setMissingTitle(false);
                      }}
                      style={{
                        width: '100%',
                        height: 50,
                        padding: '0 14px 0 42px',
                        fontSize: 14,
                        background: 'var(--color-bg)',
                        border: `1px solid ${missingTitle ? 'var(--danger)' : 'var(--color-divider)'}`,
                      }}
                    />
                  </div>
                  <p
                    id="wiz-title-help"
                    className="mm"
                    data-testid="wizard-title-help"
                    style={{ fontSize: 12, margin: '8px 0 0' }}
                  >
                    This can be a working title — you can change it later.
                  </p>
                  {/* A DISABLED BUTTON EXPLAINS NOTHING. Somebody who presses
                      Continue on an empty field learns only that nothing
                      happened; this says which field and why, where they are
                      already looking — and it sits BESIDE the help rather than
                      replacing it, because the help is still true. */}
                  {missingTitle ? (
                    <p
                      data-testid="wizard-title-error"
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        margin: '6px 0 0',
                        color: 'var(--danger)',
                      }}
                    >
                      Enter a working title to continue.
                    </p>
                  ) : null}
                </div>

                <fieldset style={{ border: 0, margin: '26px 0 0', padding: 0 }}>
                  <legend className="flbl" style={{ padding: 0 }}>
                    Production format <span style={{ color: 'var(--color-accent)' }}>*</span>
                  </legend>
                  {/* NINE OPTIONS ARE NOT A DROPDOWN. A select hides every choice
                      behind a click, which is the wrong trade when the answer
                      shapes everything after it. Real radios underneath, so arrow
                      keys and a screen reader behave as they always have. */}
                  <div className="npg-formats" style={{ marginTop: 10 }}>
                    {ProductionFormat.options.map((f) => {
                      const on = format === f;

                      return (
                        <label
                          key={f}
                          data-testid={`wizard-format-${f}`}
                          data-on={on ? '1' : '0'}
                          style={{
                            position: 'relative',
                            display: 'block',
                            padding: '14px 14px 16px',
                            cursor: 'pointer',
                            minHeight: 96,
                            background: on ? 'var(--color-surface)' : 'var(--color-bg)',
                            border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-divider)'}`,
                            boxShadow: on ? 'inset 0 0 0 1px var(--color-accent)' : 'none',
                          }}
                        >
                          <input
                            type="radio"
                            name="wizard-format"
                            value={f}
                            checked={on}
                            onChange={() => {
                              setFormat(f);
                            }}
                            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                          />
                          {/* The circle is the affordance the real radio would
                              have drawn, put where the design wants it. */}
                          <span
                            aria-hidden="true"
                            style={{
                              position: 'absolute',
                              top: 14,
                              right: 14,
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-divider)'}`,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {on ? (
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: 'var(--color-accent)',
                                }}
                              />
                            ) : null}
                          </span>
                          <span
                            style={{
                              display: 'block',
                              color: on ? 'var(--color-accent)' : 'var(--ink)',
                            }}
                          >
                            <Icon name={FORMAT_ICON[f]} size={22} />
                          </span>
                          <span
                            style={{
                              display: 'block',
                              marginTop: 18,
                              fontSize: 13,
                              fontWeight: 600,
                              lineHeight: 1.3,
                            }}
                          >
                            {PRODUCTION_FORMAT_LABELS[f]}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>

                <div style={{ marginTop: 26 }}>
                  <label className="flbl" htmlFor="wiz-model">
                    Production model <span style={{ color: 'var(--color-accent)' }}>*</span>
                  </label>
                  <div style={{ position: 'relative', marginTop: 8 }}>
                    <span
                      style={{
                        position: 'absolute',
                        left: 14,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--color-neutral-500)',
                        display: 'inline-flex',
                        pointerEvents: 'none',
                      }}
                    >
                      <Icon name="model" size={17} />
                    </span>
                    <select
                      id="wiz-model"
                      data-testid="wizard-model"
                      value={productionModel}
                      onChange={(e) => {
                        setProductionModel(e.target.value as ProductionModel);
                      }}
                      style={{
                        width: '100%',
                        height: 50,
                        padding: '0 14px 0 42px',
                        fontSize: 14,
                        fontWeight: 600,
                        background: 'var(--color-bg)',
                        border: '1px solid var(--color-divider)',
                      }}
                    >
                      {ProductionModel.options.map((m) => (
                        <option key={m} value={m}>
                          {MODEL_LABEL[m]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p
                    className="mm"
                    data-testid="wizard-model-help"
                    style={{ fontSize: 12, margin: '8px 0 0' }}
                  >
                    {MODEL_DESCRIPTION[productionModel]}
                  </p>
                </div>

              <div style={{ marginTop: 26 }}>
                <p className="flbl" style={{ margin: 0 }}>
                  Current phase
                </p>
                {/* FIVE SHORT ANSWERS, so they are all on screen: a phase is
                    the one field here that changes what the workspace LOOKS
                    like — it decides which tabs exist — and hiding that behind
                    a dropdown makes a consequential choice feel incidental. */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  {CREATABLE_LIFECYCLE_PHASES.map((phase) => (
                    <button
                      key={phase}
                      type="button"
                      className="ctl"
                      data-on={lifecyclePhase === phase ? '1' : '0'}
                      aria-pressed={lifecyclePhase === phase}
                      data-testid={`wizard-phase-${phase}`}
                      onClick={() => {
                        setLifecyclePhase(phase);
                      }}
                    >
                      {LIFECYCLE_PHASE_LABELS[phase]}
                    </button>
                  ))}
                </div>
                <p className="mm" style={{ fontSize: 12, margin: '8px 0 0' }}>
                  Where the production is today. It decides which parts of the workspace are
                  open, and you can move it whenever the production moves.
                </p>
              </div>

                {/* THE ACTIONS BELONG TO THE CARD, on their own rule: the way out
                    on the left, the way on at the right edge where the eye
                    finishes. */}
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    padding: '18px 0',
                    borderTop: '1px solid var(--color-divider)',
                    marginTop: 28,
                  }}
                >
                  {/* IT SAYS SAVE, SO IT SAVES. Nothing reaches the server before
                      the producer commits, so what is kept is a local draft —
                      enough that leaving to check a title with somebody costs
                      nothing. */}
                  <button
                    type="button"
                    className="btn btn-ghost"
                    data-testid="wizard-cancel-btn"
                    onClick={leave}
                  >
                    {title.trim() === '' ? 'Cancel' : 'Save and exit'}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    data-testid="wizard-submit"
                    disabled={!submitEnabled}
                    style={{ marginLeft: 'auto' }}
                  >
                    Continue
                  </button>
                </div>
              </div>
            </form>
          ) : null}
        </div>

        <div
          className="npg-side"
          style={{ minWidth: 0, alignSelf: payFor !== null ? 'center' : undefined }}
        >
          {art}
          {payFor === null && step === 1 ? (
          <aside data-testid="wizard-promise">
            <h2 className="dh" style={{ fontSize: 24 }}>
              From script to real places
            </h2>
            <p className="mm" style={{ fontSize: 13.5, margin: '12px 0 0', lineHeight: 1.65 }}>
              A project keeps your script, location requirements, scouting and shortlists
              connected, so your team can move from ideas to final locations.
            </p>

            <ul
              style={{ listStyle: 'none', margin: '22px 0 0', padding: 0, display: 'grid', gap: 16 }}
            >
              {PROMISES.map((promise) => (
                <li
                  key={promise.label}
                  style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 13.5 }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 34,
                      height: 34,
                      flex: 'none',
                      color: 'var(--ink)',
                    }}
                  >
                    <Icon name={promise.icon} size={22} />
                  </span>
                  <span className="mm">{promise.label}</span>
                </li>
              ))}
            </ul>
          </aside>
          ) : null}
        </div>

        {/* THE PLAN IS FULL WIDTH. Three tiers read as a comparison only when
            they sit side by side; in the form's column they would be a list of
            three separate offers. */}
        {payFor === null && step === 2 ? (
          <form
            style={{ gridColumn: '1 / -1', marginTop: 14 }}
            onSubmit={(e) => {
              e.preventDefault();

              if (!submitEnabled) return;

              create.mutate();
            }}
          >
          <h2 className="dh" style={{ fontSize: 'clamp(26px, 3.4vw, 34px)' }}>
            Choose your project plan
          </h2>
          <p className="mm" style={{ fontSize: 14, margin: '12px 0 0', lineHeight: 1.6 }}>
            Start free or unlock more tools for this production. You can change the plan later.
          </p>

          {/* THE COMMERCIAL MODEL IN ONE LINE, because a per-production charge
              is not what anyone expects from software, and the cards below
              read completely differently once it is known. */}
          <p
            data-testid="wizard-billing-note"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              margin: '22px 0 0',
              padding: '14px 18px',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-divider)',
              fontSize: 13,
              lineHeight: 1.55,
            }}
          >
            <span style={{ color: 'var(--color-accent)', display: 'inline-flex', marginTop: 1 }}>
              <Icon name="info" size={18} />
            </span>
            <span>
              <strong>Project-based billing</strong>{' '}
              <span className="mm">
                — you pay only while a project is active. Finished projects can be archived for
                free.
              </span>
            </span>
          </p>

          <QueryState query={plans} subject="the plans" testId="wizard-plans" />

          <PlanChooser
            plans={catalogue}
            chosen={chosen}
            onChoose={setChosen}
          />

          {create.isError ? (
            <p
              data-testid="wizard-error"
              style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 16 }}
            >
              {create.error.message}
            </p>
          ) : null}

          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              alignItems: 'center',
              padding: '20px 0 0',
              borderTop: '1px solid var(--color-divider)',
              marginTop: 24,
            }}
          >
            <button
              type="button"
              className="btn btn-ghost"
              data-testid="wizard-back-btn"
              onClick={() => {
                setStep(1);
              }}
            >
              <Icon name="arrowLeft" size={16} strokeWidth={2} />
              Back to project basics
            </button>

            <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
              {/* A DIFFERENT TESTID FROM STEP 1'S, because these are two
                  buttons and one of them spends money. */}
              <button
                type="submit"
                className="btn btn-primary"
                data-testid="wizard-create"
                disabled={!submitEnabled}
              >
                {create.isPending
                  ? 'Creating…'
                  : chosenPlan === null
                    ? 'Create project — Free'
                    : `Create project — ${chosenPlan.name} · ${termLabel(chosen?.term ?? 1).toLowerCase()}`}
              </button>
              <p
                className="mm"
                data-testid="wizard-plan-summary"
                style={{ fontSize: 12, margin: '8px 0 0' }}
              >
                {chosenPrice === null
                  ? 'No card needed. Paid features unlock whenever you choose a plan.'
                  : `${money(chosenPrice.amountMinor, chosenPrice.currency)} billed once for the ${termLabel(chosenPrice.termMonths).toLowerCase()} period.`}
              </p>
            </div>
          </div>
          </form>
        ) : null}
      </div>
    </div>
  );
}

/**
 * THE PRODUCTION HAS ARRIVED, AND ITS PLAN WITH IT.
 *
 * Drawn only once the card is charged AND the entitlement says the plan is
 * active — `PaymentPanel` decides that, and says what it knows until then. ONE
 * way on: the production exists and is paid for, and its Plan & billing tab is
 * where the details live, one click from where Continue lands.
 *
 * Focus moves to the heading when it appears. The button that was pressed to
 * pay is gone by now, and a screen reader would otherwise hear nothing about
 * the one outcome the producer has been waiting for.
 */
export function PlanActiveCard({
  title,
  planName,
  until,
  onContinue,
}: {
  title: string;
  planName: string;
  until: string | null;
  onContinue: () => void;
}) {
  const heading = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    heading.current?.focus();
  }, []);

  return (
    <section
      className="card1 npg-card"
      data-testid="payment-success"
      aria-labelledby="npg-created-title"
      style={{ marginTop: 24, paddingBottom: 28 }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18 }}>
        <span
          aria-hidden="true"
          style={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            flex: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--state-ok-bg)',
            color: 'var(--ok-dark)',
          }}
        >
          <Icon name="check" size={26} strokeWidth={2.4} />
        </span>
        <div style={{ minWidth: 0 }}>
          <h2
            id="npg-created-title"
            ref={heading}
            tabIndex={-1}
            className="dh"
            data-testid="payment-success-title"
            style={{ fontSize: 'clamp(26px, 3.2vw, 34px)', overflowWrap: 'anywhere', outline: 'none' }}
          >
            {title} is created
          </h2>
          <p className="mm" style={{ fontSize: 14, margin: '10px 0 0', lineHeight: 1.6 }}>
            Your project is ready and set up in the Production Workspace.
          </p>
        </div>
      </div>

      <div
        data-testid="payment-success-plan"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginTop: 26,
          padding: '18px 20px',
          background: 'var(--color-surface)',
          boxShadow: 'inset 0 0 0 1px var(--color-divider)',
        }}
      >
        <span style={{ color: 'var(--color-accent)', display: 'inline-flex' }}>
          <Icon name="layers" size={28} />
        </span>
        <div style={{ minWidth: 0 }}>
          <p className="colh" style={{ margin: 0 }}>
            Your plan
          </p>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 6 }}
          >
            <span className="dh" style={{ fontSize: 24 }}>
              {planName}
            </span>
            <StatusPill state="CONFIRMED" label="Active" testId="payment-success-state" />
          </div>
        </div>
      </div>

      <ul
        style={{ listStyle: 'none', margin: '22px 0 0', padding: 0, display: 'grid', gap: 14 }}
      >
        <li style={{ display: 'flex', alignItems: 'flex-start', gap: 14, lineHeight: 1.55 }}>
          <span style={{ color: 'var(--color-neutral-600)', display: 'inline-flex', marginTop: 1 }}>
            <Icon name="calendar" size={19} />
          </span>
          <span data-testid="payment-success-until">
            Payment has been received and the plan is active
            {until === null ? (
              '.'
            ) : (
              <>
                {' '}
                until <strong style={{ whiteSpace: 'nowrap' }}>{until}</strong>.
              </>
            )}
          </span>
        </li>
        <li style={{ display: 'flex', alignItems: 'flex-start', gap: 14, lineHeight: 1.55 }}>
          <span style={{ color: 'var(--color-neutral-600)', display: 'inline-flex', marginTop: 1 }}>
            <Icon name="bolt" size={19} />
          </span>
          <span>Everything is available now — no reload is needed.</span>
        </li>
      </ul>

      <button
        type="button"
        className="btn btn-primary"
        data-testid="payment-done"
        onClick={onContinue}
        style={{ marginTop: 28 }}
      >
        Continue
        <Icon name="arrowRight" size={16} strokeWidth={2} />
      </button>
    </section>
  );
}
